import {Trans} from '@lingui/macro';
import {keyBy} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {arrayToggle} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {RuleBundleModelStrict} from '../../../models/api/ruleBundleModel';
import {SettingsRulesUrl} from '../../../models/url/settingsUrlModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {getRuleParametersDependencies} from '../../../store/settings/settingsRulesSelectors';
import {StyledSettingsContent, StyledSettingsMain, StyledSettingsWrapper} from '../../common/settingsWrapper';
import {useFetchRuleBundles} from '../../fetchers/rulesBundles';
import {
  RuleBundleChangeActiveStatusActionsEnum,
  RuleBundleMutationsEnum,
} from '../../fetchers/rulesBundlesMutations';
import {settingsHorizontalSpacing} from '../../settingsCommonConstants';
import {AccountRulesInfoBox} from '../accountRulesInfoBox';
import {RuleHeader} from '../ruleHeader';
import {RuleListDialogs, RuleListDialogsStatesEnum} from './ruleListDialogs';
import {RuleListFilters, useRuleListSearchState} from './ruleListFilters';
import {
  buildRuleListEditUrl,
  buildRuleListItem,
  buildRuleListNewUrl,
  RuleListItem,
  RuleListTableSortsEnum,
} from './ruleListHelpers';
import {useRuleListInfoBox} from './ruleListInfoBox';
import {RuleListTable} from './ruleListTable';

/*
 * Props.
 */

interface RuleListProps {
  rulesUrl: SettingsRulesUrl;
}

const StyledTopLine = styled.div`
  flex: 1;
  padding-left: ${settingsHorizontalSpacing}px;
  margin-bottom: 16px;

  display: grid;
  grid-template-columns: auto min-content;
`;

/*
 * Constants.
 */

const sortToSortFunction: Record<
  RuleListTableSortsEnum,
  (itemA: RuleListItem, itemB: RuleListItem) => number
> = {
  [RuleListTableSortsEnum.NAME_ASC]: (itemA, itemB) => itemA.name.localeCompare(itemB.name),
  [RuleListTableSortsEnum.NAME_DESC]: (itemA, itemB) => itemB.name.localeCompare(itemA.name),
  [RuleListTableSortsEnum.ORDER_ASC]: (itemA, itemB) => itemA.order - itemB.order,
  [RuleListTableSortsEnum.ORDER_DESC]: (itemA, itemB) => itemB.order - itemA.order,
  [RuleListTableSortsEnum.ACTIVE_ASC]: (itemA, itemB) => {
    if (itemA.isActive === itemB.isActive) {
      return itemA.name.localeCompare(itemB.name);
    }
    return itemA.isActive ? 1 : -1;
  },
  [RuleListTableSortsEnum.ACTIVE_DESC]: (itemA, itemB) => {
    if (itemA.isActive === itemB.isActive) {
      return itemA.name.localeCompare(itemB.name);
    }
    return itemA.isActive ? -1 : 1;
  },
  [RuleListTableSortsEnum.LAST_APPLIED_ASC]: (itemA, itemB) =>
    (itemA.lastApplied ?? 0) - (itemB.lastApplied ?? 0),
  [RuleListTableSortsEnum.LAST_APPLIED_DESC]: (itemA, itemB) =>
    -(itemA.lastApplied ?? 0 - (itemB.lastApplied ?? 0)),
};

/*
 * Component.
 */

export const RuleList: FC<RuleListProps> = ({rulesUrl}) => {
  const [searchState, setSearchState] = useRuleListSearchState(rulesUrl);
  const [ruleSort, setRuleSort] = useState<RuleListTableSortsEnum>(RuleListTableSortsEnum.ORDER_ASC);
  const company = useAppSelector(getCompany);
  const {namespace} = rulesUrl;
  const {navigateToLink} = useLinkNavigation();
  const [multiSelectionItemsIds, setMultiSelectionItemsIds] = useState<ReadonlyArray<number>>([]);
  const [bundleToDeleteId, setBundleToDeleteId] = useState<number>();
  const [bulkDialogsState, setBulkDialogsState] = useState<RuleListDialogsStatesEnum>();
  const {items, loadMore, moreStatus, mutate} = useFetchRuleBundles(namespace, searchState, ruleSort);
  const idToRuleBundle = useMemo(() => keyBy(items, 'id'), [items]);
  const {infoBox, infoBoxIcon} = useRuleListInfoBox();

  const displayedItems = useDisplayedItems(items, ruleSort);

  const targetItems = useMemo(() => {
    const targetItemsIds = isDefined(bundleToDeleteId) ? [bundleToDeleteId] : multiSelectionItemsIds;
    return displayedItems.filter((item) => item.id && targetItemsIds.includes(item.id));
  }, [bundleToDeleteId, displayedItems, multiSelectionItemsIds]);

  const dialogsState = useMemo(
    () => (isDefined(bundleToDeleteId) ? RuleListDialogsStatesEnum.DELETE : bulkDialogsState),
    [bulkDialogsState, bundleToDeleteId],
  );

  const bulkActionsPayload = useMemo(
    () => ({
      bundles: targetItems.map((item) => idToRuleBundle[item.id]),
      namespace,
      company,
    }),
    [company, idToRuleBundle, namespace, targetItems],
  );

  const resetSelection = () => {
    setMultiSelectionItemsIds([]);
  };

  return (
    <StyledSettingsWrapper>
      <RuleListDialogs
        dialogsState={dialogsState}
        targetItems={targetItems}
        onSetActive={() => {
          mutate({
            type: RuleBundleMutationsEnum.CHANGE_ACTIVE_STATUS,
            payload: {...bulkActionsPayload, action: RuleBundleChangeActiveStatusActionsEnum.SET_ACTIVE},
          });
          resetSelection();
        }}
        onSetInactive={() => {
          mutate({
            type: RuleBundleMutationsEnum.CHANGE_ACTIVE_STATUS,
            payload: {...bulkActionsPayload, action: RuleBundleChangeActiveStatusActionsEnum.SET_INACTIVE},
          });
          resetSelection();
        }}
        onDelete={() => {
          mutate({type: RuleBundleMutationsEnum.DELETE, payload: bulkActionsPayload});
          resetSelection();
        }}
        onClose={() => {
          setBulkDialogsState(undefined);
          setBundleToDeleteId(undefined);
        }}
      />
      <RuleHeader rulesUrl={rulesUrl} right={infoBoxIcon}>
        <Trans id="settings-rules-header-title">Rules</Trans>
      </RuleHeader>
      <StyledSettingsMain>
        <StyledSettingsContent>
          <StyledTopLine>
            <RuleListFilters value={searchState} onChange={setSearchState} />
            <Button
              onClick={() => navigateToLink(buildRuleListNewUrl(rulesUrl))}
              variant={ButtonVariantsEnum.PRIMARY}
            >
              <Trans id="rule-list-add-rule">Add a rule</Trans>
            </Button>
          </StyledTopLine>
          <AccountRulesInfoBox namespace={namespace} />
          <RuleListTable
            rulesUrl={rulesUrl}
            items={displayedItems}
            selectedItemsIds={multiSelectionItemsIds}
            onLoadMore={loadMore}
            moreStatus={moreStatus}
            ruleSort={ruleSort}
            onRuleSortChange={setRuleSort}
            onRuleSelect={() => {}}
            onRuleSelectedToggle={(id) => setMultiSelectionItemsIds(arrayToggle(multiSelectionItemsIds, id))}
            onRuleEdit={(rule) => navigateToLink(buildRuleListEditUrl(rulesUrl, rule))}
            onRuleDelete={(bundle) => setBundleToDeleteId(bundle.id)}
            onRuleReorder={(rule, newOrder) => {
              const normalizedNewOrder = Math.max(1, newOrder);
              const bundle = idToRuleBundle[rule.id];

              if (normalizedNewOrder === rule.order || !bundle) {
                return;
              }

              mutate({
                type: RuleBundleMutationsEnum.REORDER,
                payload: {
                  bundle,
                  previousOrder: rule.order,
                  newOrder: normalizedNewOrder,
                  company,
                },
              });
            }}
            onRuleToggleActive={(ruleItems) =>
              mutate({
                type: RuleBundleMutationsEnum.CHANGE_ACTIVE_STATUS,
                payload: {
                  bundles: ruleItems
                    .map((ruleItem) => items.find((item) => item.id === ruleItem.id))
                    .filter(isDefined),
                  company,
                  namespace,
                  action: RuleBundleChangeActiveStatusActionsEnum.TOGGLE_ACTIVE,
                },
              })
            }
            onBulkActivateClick={() => setBulkDialogsState(RuleListDialogsStatesEnum.ACTIVATE)}
            onBulkDeactivateClick={() => setBulkDialogsState(RuleListDialogsStatesEnum.DEACTIVATE)}
            onBulkDeleteClick={() => setBulkDialogsState(RuleListDialogsStatesEnum.DELETE)}
            onHeaderMultiSelect={(isSelectingAll) =>
              setMultiSelectionItemsIds(isSelectingAll ? displayedItems.map((item) => item.id) : [])
            }
          />
        </StyledSettingsContent>
        {infoBox}
      </StyledSettingsMain>
    </StyledSettingsWrapper>
  );
};

/*
 * Helpers.
 */

function useDisplayedItems(items: ReadonlyArray<RuleBundleModelStrict>, sort: RuleListTableSortsEnum) {
  const ruleParameterDependencies = useAppSelector(getRuleParametersDependencies);
  const localize = useLocalize();

  const listItems = useMemo(
    () => items.map((item) => buildRuleListItem(item, ruleParameterDependencies, localize)).filter(isDefined),
    [items, localize, ruleParameterDependencies],
  );

  return useMemo(() => listItems.sort(sortToSortFunction[sort]), [listItems, sort]);
}
